import {
  Headline,
  NotificationBanner,
  RadiobuttonCard,
  RadiobuttonCardGroup,
  Text,
} from '@velocity/ui';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { updateCustomerData } from '../../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../../redux/store';
import { RootState } from '../../../../redux/types';
import { APIPaths } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import { errorLogging } from '../../../../utils/logging';
import Navigation from '../../../../utils/navigation';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import CoApplicant from '../co-applicant/co-applicant';

const HasCoApplicant: React.FC = () => {
  const { activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );
  const { accessToken } = useSelector((state: RootState) => state.auth);

  const navigate = useNavigate();
  const { nextRoute } = new Navigation();
  const dispatch = useAppDispatch();

  const leaseApplicationId = activeApplication?.uuid || '';
  const signers = activeApplication?.customer?.private?.signers;

  // derive initial state once
  const initialHasCoApplicant = useMemo<boolean | undefined>(() => {
    if (!signers || signers.length === 0) return undefined;
    return signers.length > 1;
  }, [signers]);

  const [hasCoApplicant, setHasCoApplicant] = useState<boolean | undefined>(
    initialHasCoApplicant,
  );

  const updateCoApplicant = async (value: boolean) => {
    try {
      await dispatch(
        updateCustomerData({
          accessToken,
          leaseApplicationId,
          inputValue: { is_co_applicant_present: value },
          path: APIPaths.UPDATE_MAIN_SIGNER,
        }),
      );
    } catch (e) {
      errorLogging(e as Error);
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Das tut uns leid',
          notificationBody: getText('error_unable_to_process_data'),
        }),
      );
    }
  };

  const handleSubmit = async () => {
    if (hasCoApplicant === false) {
      await updateCoApplicant(false);
    }
    navigate(nextRoute());
  };

  const handleSelection = async (value: boolean) => {
    setHasCoApplicant(value);
    await updateCoApplicant(value);
  };

  const selectedValue =
    hasCoApplicant === undefined ? '' : hasCoApplicant ? 'yes' : 'no';

  // Figma: label text is 16px/20px secondary-400 (body/text-tight-200)
  const cardLabel = (children: React.ReactNode) => (
    <Text
      component='span'
      variant='200'
      className='text-(--vel-color-secondary-400)!'
    >
      {children}
    </Text>
  );

  // Figma: "formkit/radiobutton-card" - 308px wide, 20px/16px padding
  const cardClassName = 'w-full! max-w-77 px-5! py-4!';

  return (
    <Layout
      heading='Ein Mitantragssteller erhöht deine Chancen auf eine Zusage'
      stepStatus
      subHeading='Du kannst optional eine weitere Person hinzufügen.'
      className='choice-modal layout-bar-bottom'
    >
      <div className='flex w-full flex-col gap-6'>
        {hasCoApplicant && (
          <NotificationBanner
            kind='information'
            title='Mitantragssteller hinzufügen'
          >
            Ein Mitantragssteller kann deine Bonität verbessern und deine
            Chancen auf eine Zusage erhöhen.
          </NotificationBanner>
        )}

        <div className='flex flex-col gap-6'>
          <Headline variant='200' component='h2' id='has-co-applicant-label'>
            Mitantragssteller hinzufügen
          </Headline>
          <RadiobuttonCardGroup
            name='has_co_applicant'
            value={selectedValue}
            onChange={(event) => handleSelection(event.target.value === 'yes')}
            aria-labelledby='has-co-applicant-label'
            data-testid='radio-field-has_co_applicant'
          >
            <RadiobuttonCard
              value='yes'
              details={cardLabel('Mitantragssteller hinzufügen')}
              className={cardClassName}
              data-testid='radio-has_co_applicant-yes'
            />
            <RadiobuttonCard
              value='no'
              details={cardLabel(
                <>
                  Weiter <b>ohne</b> Mitantragssteller
                </>,
              )}
              className={cardClassName}
              data-testid='radio-has_co_applicant-no'
            />
          </RadiobuttonCardGroup>
        </div>

        {hasCoApplicant && <CoApplicant hasCoApplicant />}
      </div>

      {!hasCoApplicant && (
        <ButtonGroup
          className='back-next'
          type='back-next'
          data-testid='disabled-forward-button'
          buttonOneProps={{
            type: 'button',
            disabled: hasCoApplicant === undefined,
            onClick: handleSubmit,
            name: 'form-submit',
            dataTestId: 'next',
            children: 'nächster Schritt',
          }}
        />
      )}
    </Layout>
  );
};

export default HasCoApplicant;
